import { BehaviorSubject, interval, Observable, of, Subscription, timer } from 'rxjs';
import { ChangeDetectorRef, Component, ElementRef, HostListener, Input, OnChanges, OnInit, QueryList, ViewChild, ViewChildren } from '@angular/core';
import { Time, DateUtil, GetTimeline, TimelineItem, TimelineType, TimelineData, TimelineStateModel, TimelineResponse, ScoredSubscription, CommonService } from 'huddyapi';

import { Select, Store } from '@ngxs/store';
import { GraphDetail } from '../graph-detail/graph-detail.component';
import { MatDialog } from '@angular/material/dialog';
import { DocumentViewer } from '../document-viewer/document-viewer.component';

@Component({
  selector: 'timeline',
  templateUrl: './timeline.component.html',
  styleUrls: ['./timeline.component.scss']
})
export class Timeline implements OnInit, OnChanges {

  @Input('scoredSubscriptions') scoredSubscriptions : ScoredSubscription[];
  @Input('patientId') patientId : number;
  @Input('patientName') patientName : string;
  @ViewChild('graphbox', { static: true }) container: ElementRef;
  @ViewChild('popover', { static: true }) popover: ElementRef;
  @Select(state => state.timelineState) timelineState$: Observable<TimelineStateModel>;
  timelineSubscription : Subscription;

  datePickerDate = new Date();
  rangeStartDate : Date;
  rangeEndDate : Date;
  showScrolling : boolean;
  currentRange : string = 'earliest';
  labelBackground : Observable<any> = of({
    width: Timeline.TEXT_WIDTH,
    height: Timeline.CELL_WIDTH,
    fill: 'white'
  });
  objectTypes : string[] = ['1', '2', '3'];
  days : number[] = Array(30).fill(30).map((x,i)=>i);
  maxDays : number[] = Array(Timeline.MAX_DAYS_SHOWN).fill(Timeline.MAX_DAYS_SHOWN).map((x,i)=>i)

  selectedTypes : string[];
  allTypes : string[];
  startDate : Time = new Time();
  

  circles :  BehaviorSubject<any>[][] = [];
  layers : BehaviorSubject<any>[] = [];
  labelLayers : BehaviorSubject<any>[] = [];
  dateLayer : BehaviorSubject<any>[] = [];
  readyToRender : boolean = false;
  
  rowWidth : number = this.days.length * Timeline.CELL_WIDTH;
  stageConfig : BehaviorSubject<any> = new BehaviorSubject({
    width: Timeline.DOUBLE_BOX_PADDING + this.rowWidth ,
    height: (Timeline.CELL_WIDTH + 1) * (Timeline.MAX_OBJECT_TYPE_LEN + 1) + 20
  });
  labelStageConfig : BehaviorSubject<any> = new BehaviorSubject({
    width:  Timeline.TEXT_WIDTH,
    height: (Timeline.CELL_WIDTH + 1) * (Timeline.MAX_OBJECT_TYPE_LEN + 1) + 20
  });
  maxWidth : number = Timeline.MAX_DAYS_SHOWN * Timeline.CELL_WIDTH;
  requestedPatientId : number ;
  lines : BehaviorSubject<any>[] = [];
  textLabels : BehaviorSubject<any>[] = [];
  timelineData : TimelineResponse;
  dateRowLayer: BehaviorSubject<any> = new BehaviorSubject({
    width: Timeline.DOUBLE_BOX_PADDING + this.rowWidth ,
    height: (Timeline.CELL_WIDTH + 1),
    y: (5 ) * (Timeline.CELL_WIDTH + 1)
  });
  popoverMap : Map<TimelineItem, TimelineData[]>  = new Map();

  static CELL_WIDTH : number = 44;
  static HALF_CELL : number = Timeline.CELL_WIDTH / 2;
  static CIRCLE_PADDING : number = 6;
  static DIAMETER : number = Timeline.CELL_WIDTH - (2 * Timeline.CIRCLE_PADDING);
  static RADIUS : number = Timeline.DIAMETER / 2;
  static TEXT_WIDTH : number = 4 * Timeline.CELL_WIDTH;
  static MAX_DAYS_SHOWN : number = 1000;
  static BOX_PADDING: number = 16;
  static MAX_OBJECT_TYPE_LEN : number = 25;
  static DOUBLE_BOX_PADDING: number = 2 * Timeline.BOX_PADDING;

  lastX : number;
  lastY : number;

  constructor(public cd : ChangeDetectorRef,
    public dialog: MatDialog,
    public store : Store) {
    this.startDate.subtract(30, 'days'); 

    this.  timelineSubscription  = this.timelineState$.subscribe((ts: TimelineStateModel) => {
      console.log(ts);
      if (ts && ts.lastResponse) {
         
         console.log('Timeline state: ',ts.lastResponse);   
         this.timelineData = ts.lastResponse;
         this.configure();   
      }
    });
    this.initArrays();
  }
  buildObjectTypesFromData() {
    this.objectTypesMap.clear();
    
    this.timelineData.items.forEach(item => {
      //console.log('objectTypesMap', item.type.toString());
      let itemType : string = item.type.toString();
      switch(itemType) {
        case "SYMPTOM":
          this.objectTypesMap.set("SYMPTOM", "Symptoms");
          break;
        case "EVIDENCE":
            this.objectTypesMap.set("EVIDENCE", "Evidence");
            break;
        case "TREATMENT":
          this.objectTypesMap.set("TREATMENT", "Treatments");
          break;
        case "ACTION":
          this.objectTypesMap.set("ACTION", "Actions");
          break;
        case "COMPASS_SCORE":
          this.objectTypesMap.set("COMPASS_SCORE", "Compass Scores");
          break;
        case "ADVERSE_EVENT":
          this.objectTypesMap.set("ADVERSE_EVENT", "Adverse Events");
          break;
        case "ALLERGY_INTOLERANCE":
          this.objectTypesMap.set("ALLERGY_INTOLERANCE", "Allergy Intolerances");
          break;
        case "CLAIM":
          this.objectTypesMap.set("CLAIM", "Claims");
          break;
        case "CARE_PLAN":
          this.objectTypesMap.set("CARE_PLAN", "Care Plans");
          break;
        case "GOAL":
          this.objectTypesMap.set( "GOAL", "Goals");
          break;
        case "CONDITION_DIAGNOSIS":
          this.objectTypesMap.set("CONDITION_DIAGNOSIS", "Diagnoses");
          break;
        case "DIAGNOSTIC_REPORT":
          this.objectTypesMap.set("DIAGNOSTIC_REPORT", "Reports");
          break;
        case "DOCUMENT":
          this.objectTypesMap.set("DOCUMENT", "Documents");
          break;
        case "ENCOUNTER":
          this.objectTypesMap.set("ENCOUNTER", "Encounters");
          break;
        case "IMMUNIZATION":
          this.objectTypesMap.set("IMMUNIZATION", "Immunizations");
          break;
        case "MEDICATION":
          this.objectTypesMap.set("MEDICATION", "Medications");
          break;
        case "MEDICATION_ORDER":
            this.objectTypesMap.set("MEDICATION_ORDER", "Medication Orders");
            break;
        case "NUTRITION_ORDER":
          this.objectTypesMap.set("NUTRITION_ORDER", "Nutrition Orders");
          break;
        case "PROCEDURE":
          this.objectTypesMap.set('PROCEDURE', "Procedures");
          // console.log('procedures 1', this.objectTypesMap);
          break;
        case "OBSERVATION":
          this.objectTypesMap.set('OBSERVATION', "Observations");
          // console.log('objectTypesMap 1', this.objectTypesMap);
          break;
        default:  
      }
    });
    this.allTypes = Array.from(this.objectTypesMap.values());
    console.warn("SELECTEDTYPES", this.selectedTypes);
    if (this.selectedTypes) {
      let remove: string[] = [];
      this.objectTypesMap.forEach((v: string, k: string) => {
          if (this.selectedTypes.indexOf(v) === -1) {
            remove.push(k);
          }
      });
      for (let k of remove) {
        this.objectTypesMap.delete(k);
      }
    }
    console.warn('OBJECTTYPESMAP', this.objectTypesMap.keys());
  }
 
  ngOnChanges(changes){
    console.log('changes', changes, this.scoredSubscriptions, this.patientId);
    if (this.scoredSubscriptions  && (this.requestedPatientId != this.patientId)) {
      let numDays = new Time().getEpochDay()+1;
      console.log('ngOnChanges', numDays, this.patientId)
      this.store.dispatch(new GetTimeline(this.patientId, -1, numDays));//
      this.readyToRender = false;
      this.requestedPatientId = this.patientId;
      this.configure();
    }
  }

  hasMedicationType(day : number, objectTypeLabel : string) {
    let objectType : TimelineType = this.getTypeFromLabel(objectTypeLabel);
    if (objectType === 'CLAIM') {
      let epochDay = this.getEpochDayForDay(day);
      
      let data : TimelineData[] = this.timelineData.epochDaysToData.get(epochDay)
      .filter(t => this.isObjectType(t, objectType) && t.med);
      console.log('HasMedicationType', 'epochDay', epochDay, data);
      return data.length > 0;
    }
    return false;
  }

  showDay(component, day: number, objectTypeLabel: string) {
    this.popoverMap.clear();
    let objectType : TimelineType = this.getTypeFromLabel(objectTypeLabel);
    let epochDay = this.getEpochDayForDay(day);
    if ( this.timelineData && this.timelineData.epochDaysToData.has(epochDay)) {
      let data : TimelineData[] = this.timelineData.epochDaysToData.get(epochDay)
       .filter(t => this.isObjectType(t, objectType));
       console.log('showDay',  day, objectType, objectTypeLabel); 
       if (data.length > 0) {
        for (let item of this.timelineData.items) {
          let itemArr : TimelineData[] = data.filter(d => d.itemId === item.id).sort((a, b) => {
            console.log('showDay', (typeof a.dateTime));
            let dateA : number = (typeof a.dateTime === 'string') ? new Time(a.dateTime).toDate().getTime() : a.dateTime.getTime();
            let dateB : number = (typeof b.dateTime === 'string') ? new Time(b.dateTime).toDate().getTime() : b.dateTime.getTime();
            return dateA - dateB;
          });
          
          if (itemArr.length >0) {
            console.log('showDay2', this.isGraphable(item.type), item, itemArr);
            this.popoverMap.set(item, itemArr);
          }
        }
      }
    }
   
    if (this.popoverMap.size > 0) {
      console.log('showDay', this.popover);
     this.popover.nativeElement.style = 'position:absolute;visibility:visible;top:' + this.lastY + 'px;left:' + this.lastX + 'px;'
    } else {
      this.popover.nativeElement.style = 'position:absolute;visibility:hidden;';
    }
  }

  hidePopover() {
    this.popover.nativeElement.style = 'position:absolute;visibility:hidden;';
  }

  getTypeFromLabel(label : string) {
    let ret : string = undefined;
    this.objectTypesMap.forEach((v,k) => {
      if (v === label) {
        ret = k;
      }
    });
    if (ret) {
      return TimelineType[ret];
    }
    return undefined;
  }

  isObjectType(data : TimelineData, objectType : TimelineType) {
    let item : TimelineItem = this.getItemById(data.itemId);
    let itemType : TimelineType = this.stringToItemType(item.type);
    return item && itemType === objectType;
  }

  getItemById(itemId : number) : TimelineItem {
    let ret : TimelineItem = undefined;
    if (this.timelineData) {
      let filtered  = this.timelineData.items.filter((item: { id: number; }) => item.id === itemId);
      if (filtered.length > 0) {
        ret = filtered[0];
      }
    }
    return ret;
  }


  initArrays() {
    this.lines = this.getLines();
    for (let i =0; i < Timeline.MAX_OBJECT_TYPE_LEN; i++) {
      this.layers.push( new BehaviorSubject({
        width: this.maxWidth,
        height: (Timeline.CELL_WIDTH + 1),
        y: i * (Timeline.CELL_WIDTH + 1)
      }));
      
      this.labelLayers.push( new BehaviorSubject({
        width: Timeline.TEXT_WIDTH,
        height: (Timeline.CELL_WIDTH + 1),
        y: i * (Timeline.CELL_WIDTH + 1)
      }));
      this.textLabels.push( this.configTextLabel(''));
      let arr : BehaviorSubject<any>[] = [];
      for (let j = 0; j < Timeline.MAX_DAYS_SHOWN; j++) {
        arr.push(new BehaviorSubject({
          x: Timeline.DOUBLE_BOX_PADDING + Timeline.RADIUS + (j * Timeline.CELL_WIDTH) ,
          y: Timeline.HALF_CELL,
          radius: Timeline.RADIUS,
          opacity: 0,
          fill:  '#A3D3FF',
          stroke: '#A3D3FF',
          strokeWidth: 0
        })); 
      }
      this.circles.push(arr);
    }
    
    for (let i = 0; i < Timeline.MAX_DAYS_SHOWN; i++) {
      this.dateLayer.push(new BehaviorSubject({}));
    }
  }

  onTypeSelected() {
    // console.log('onTypeSelected', this.selectedTypes);
    this.configure();
  }

  hasData(key : string, index : number) : boolean {
    let ret :boolean = false;
    if (!key) {
      return ret;
    }
 
    let epochDay = this.getEpochDayForDay(index);
    //  console.log('hasData', index, key, epochDay,  this.timelineData.epochDaysToData.get(epochDay));
    if ( this.timelineData && this.timelineData.epochDaysToData.has(epochDay)) {
      for (let data of this.timelineData.epochDaysToData.get(epochDay)) {
        let itemId : number = data.itemId;
        
        let items : TimelineItem[] = this.timelineData.items.filter(tlItem => tlItem.id === itemId);
        if (items.length > 0) {
          ret = (items[0].type === key);
        }  
        //  if ( epochDay == 16771) {
        //    console.log('hasData', ret,  (items[0].type === key), itemId, items[0].type, this.stringToItemType(key), key);
        //  }

        if (ret) {
          break;
        }

      }

    }
    return ret;
  }



  getRelevantTypes() : string[] {
    let ret : string[] = (this.selectedTypes && this.selectedTypes.length > 0) ? this.selectedTypes : this.allTypes;
    ret.sort();
    return ret;
  }

  getKeyForLabel(label : string) {
    let ret : string = null;
    this.objectTypesMap.forEach((v, k) => {
      if (v === label) {
        ret = k;
      }
    });
    return ret;
  }



   getLabelLayer(index : number) : BehaviorSubject<any> {
    return new BehaviorSubject({
      width:  Timeline.TEXT_WIDTH,
      height: (Timeline.CELL_WIDTH + 1),
      y: index * (Timeline.CELL_WIDTH )
    })
   } 

  getEpochDayForDay(day : number) {
    if (this.timelineData) {
      return this.timelineData.range.min + day;
    }
    return 0;
  }


  configRowSeparator()  {
    return of({
      points : [Timeline.DOUBLE_BOX_PADDING , Timeline.CELL_WIDTH,  Timeline.DOUBLE_BOX_PADDING + this.rowWidth, Timeline.CELL_WIDTH],
      stroke: 'rgba(0, 0, 0, .24)',
      strokeWidth: 1
    });
  }

  configTextLabel(text : string): BehaviorSubject<any> { 
    return new BehaviorSubject({
      fontSize: 12,
      fill: '#878787',
      text: text,
      fontFamily: 'Arial',
      x: 0,
      y: Timeline.HALF_CELL - 8
    });
    //
  }



  getDateString(day : number) : string{
    let t : Time = new Time(this.startDate.getDateTimeDisplay());
    if (day > 0)
      t.add(day, 'days');

    //console.log('getDateString', day, t.getDateDisplay());
    return  t.getMonth() + ' ' + t.getDay() + ' ' +  t.momentVal.year();
  }

  ngOnInit() {
   
    
  }

  getCellClass(category : string, day : number) {
    let ret : string = 'no-day-data';
    const key : string = this.getKeyForLabel(category);
    if (this.hasData(key, day)) {
      if (key === 'SYMPTOM' ) {
        ret = 'symptom-poor';
        const average : number = this.getAverageSymptomsForDay(day);
        // console.log('symptom average for day', day, average);
        if (average < 20) {
          ret = 'symptom-good';
        } else if (average > 19 && average < 40) {
          ret = 'symptom-average';
        } else if (average > 39 && average < 60) {
          ret = 'symptom-fair';
        } else if (average > 59 && average < 80) {
          ret = 'symptom-mediocre';
        } 
      } else {
        ret = 'day-data';
      }
    }
    return ret;
  }

  getSymptomWord(value : number) {
    if (value > 66) {
      return 'A lot';
    } else if (value < 67 && value > 33) {
      return 'Some';
    }
    return 'None';
  }

  getSymptomColorStyle(value : number) {
    let ret : string = 'poor';
    if (value < 20) {
      ret = 'good';
    } else if (value > 19 && value < 40) {
      ret = 'average';
    } else if (value > 39 && value < 60) {
      ret = 'fair';
    } else if (value > 59 && value < 80) {
      ret = 'mediocre';
    } 
    return ret;
  }

  getAverageSymptomsForDay(day: number): number {
    let epochDay = this.getEpochDayForDay(day);
    const data : TimelineData[] = this.timelineData.epochDaysToData.get(epochDay);
    const items : TimelineItem[] = this.timelineData.items;
    let symptoms : TimelineData[] = data.filter(d => {
      const item : TimelineItem = items.find(it => it.id === d.itemId);
      return item.type === TimelineType.SYMPTOM;
    }); 
    var values = Object.entries(symptoms).map(el => el[1].value);
    return (values.reduce((a,c) => a + c) / values.length);
  }

  @HostListener('window:resize', ['$event'])
onResize(event) {

}

getLines() : BehaviorSubject<any>[] {
  let ret : BehaviorSubject<any>[] = [];
  let boxBottom : number = Timeline.CELL_WIDTH * (Timeline.MAX_OBJECT_TYPE_LEN + 1) + Timeline.BOX_PADDING;
  //topline
  ret.push( new BehaviorSubject({
    points :[Timeline.BOX_PADDING, 1,  Timeline.DOUBLE_BOX_PADDING + this.rowWidth, 1],
    stroke: 'rgba(0, 0, 0, .24)',
    strokeWidth: 1
  }));
  //leftline
  ret.push(new BehaviorSubject ({
    points :[Timeline.BOX_PADDING, 1,  Timeline.BOX_PADDING, boxBottom],
    stroke: 'rgba(0, 0, 0, .24)',
    strokeWidth: 1
  }));
    //bottom line
  ret.push(new BehaviorSubject ({
      points :[  Timeline.BOX_PADDING, boxBottom, Timeline.DOUBLE_BOX_PADDING + this.rowWidth, boxBottom],
      stroke: 'rgba(0, 0, 0, .24)',
      strokeWidth: 1
    }));
    //date gap line (example)
    let x: number =  (3 * Timeline.CELL_WIDTH) - Timeline.CIRCLE_PADDING;
    ret.push(new BehaviorSubject ({
      points :[Timeline.DOUBLE_BOX_PADDING + x, 1, Timeline.DOUBLE_BOX_PADDING + x, boxBottom],
      dash: [10, 5],
      stroke: 'rgba(0, 0, 0, .48)',
      strokeWidth: 1
    }));
  return ret;
}

 
  onRangeSelected() {
    //console.log( this.currentRange);
    let today : number = new Time().getEpochDay(); //
    let start : number = today - 29;
    let end : number = today;

    if (this.currentRange === 'last90') {
      start = today - 89;
    } else if (this.currentRange === 'lastYear') {
      start = today - 364;
    } else if (this.currentRange === 'earliest') {
      start = -1;
    } else if (this.currentRange === 'custom') {
      start = (this.rangeStartDate) ? new Time(this.rangeStartDate.toISOString()).getEpochDay() : 0;
      end =  (this.rangeEndDate) ? new Time(this.rangeEndDate.toISOString()).getEpochDay() : 0;
    }


    let numDays = Math.max(30, end - start + 1);
    this.store.dispatch(new GetTimeline(this.patientId, start, start + numDays));

  }

  configure() {
    if (!this.timelineData || !this.scoredSubscriptions)
      return;
   
    let start : number = this.timelineData.range.min;
    let numDays : number = this.timelineData.range.max - start;
    this.buildObjectTypesFromData();
    let daysWithData : number[] = Array.from(this.timelineData.epochDaysToData.keys()).map(s =>+s).sort();
    // console.log('configure', 'daysWithData', daysWithData)
    // if (daysWithData.length > 30 || numDays > 90) {
      start = Math.min(...daysWithData);
      let end : number = Math.max(...daysWithData);
      numDays = end - start;
      if (numDays > 30) {
        numDays = daysWithData.length;
        // console.log('configure daysWithData', this.startDate, start, numDays,this.days, daysWithData)
        this.days = Array(numDays).fill(numDays).map((x,i)=>daysWithData[i] - start);
      } else if (numDays > 0) {
        this.days  = Array(numDays).fill(numDays).map((x,i)=>i);
      }

    // } else {
    //   this.days  = Array(numDays).fill(numDays).map((x,i)=>i);
    // }
    this.startDate = DateUtil.fromEpochDay(start);
    // console.log('configure daysWithData', this.startDate, start, numDays,this.days);

    this.rowWidth  = this.days.length * Timeline.CELL_WIDTH;
    let clientWidth : number = this.container.nativeElement.clientWidth ;
    
    
    this.showScrolling = this.rowWidth > clientWidth;  
    // console.log(this.days);
    
    this.cd.detectChanges();
    timer(1000).subscribe(t => this.readyToRender = true);
    // this.redraw();
  }

  mouseCapture($event) {
    this.lastX = $event.pageX;
    this.lastY = $event.pageY;
    //console.log('mouseCapture', $event, this.lastX, this.lastY);
  }

  onCustomRangeDateChange($event: any) {
    console.log( this.currentRange, this.rangeStartDate, this.rangeEndDate, $event);
    if (this.currentRange === 'custom' && this.rangeStartDate && this.rangeEndDate) {
      let start = new Time(this.rangeStartDate.toISOString()).getEpochDay();
      let end =  new Time(this.rangeEndDate.toISOString()).getEpochDay();
      let numDays = Math.max(30, end - start + 1);
      this.store.dispatch(new GetTimeline(this.patientId, start, start + numDays));
    }
  }

  getBorderBoxClass(n : number) {
   let ret : string = 'border-box-solid';
   if (n > 0) {
    let current : number = this.days[n];
    let previous : number = this.days[n-1];
    ret = (1 < current - previous) ? 'border-box-dashed' : 'border-box-none';
   }
   
   return ret;
  }
  objectTypesMap : Map<string, string> =  new Map();

  stringToItemType(itemType : any) : TimelineType {
    itemType = ''+itemType;
    let ret : TimelineType = null;
    switch(itemType) {
      case "SYMPTOM":
        ret = TimelineType.SYMPTOM;
        break;
      case "TREATMENT":
        ret = TimelineType.TREATMENT;
        break;
      case "EVIDENCE":
        ret = TimelineType.EVIDENCE;
        break;
      case "ACTION":
        ret = TimelineType.ACTION;
        break;
      case "COMPASS_SCORE":
        ret = TimelineType.COMPASS_SCORE;
        break;
      case "PROCEDURE":
        ret = TimelineType.PROCEDURE;
        break;
      case "ADVERSE_EVENT":
        ret = TimelineType.ADVERSE_EVENT;
        break;
      case "ALLERGY_INTOLERANCE":
        ret = TimelineType.ALLERGY_INTOLERANCE;
        break;
      case "CLAIM":
        ret = TimelineType.CLAIM;
        break;
      case "CARE_PLAN":
        ret = TimelineType.CARE_PLAN;
        break;
      case "GOAL":
        ret = TimelineType.GOAL;
        break;
      case "CONDITION_DIAGNOSIS":
        ret = TimelineType.CONDITION_DIAGNOSIS;
        break;
      case "DIAGNOSTIC_REPORT":
        ret = TimelineType.DIAGNOSTIC_REPORT;
        break;
      case "DOCUMENT":
        ret = TimelineType.DOCUMENT;
        break;
      case "ENCOUNTER":
        ret = TimelineType.ENCOUNTER;
        break;
      case "IMMUNIZATION":
        ret = TimelineType.IMMUNIZATION;
        break;
      case "MEDICATION":
        ret = TimelineType.MEDICATION;
        break;
      case "NUTRITION_ORDER":
        ret = TimelineType.NUTRITION_ORDER;
        break;
      case "MEDICATION_ORDER":
        ret = TimelineType.MEDICATION_ORDER;
        break;
      case "OBSERVATION":
        ret = TimelineType.OBSERVATION;
        break;
      default:  
    }
    //console.log('stringtoItemType', itemType, ret);
    return ret;
  }



  showGraphDetail(type : string, timelineItem? : TimelineItem, timelineDataArr? : TimelineData[]) {
    let width = (.9 * window.innerWidth) + 'px';   
    let height = (.9 * window.innerHeight) + 'px';  
    this.hidePopover();
    console.log('showing', type, timelineItem);
    let items : TimelineItem[] = this.timelineData.items.filter(item => this.isGraphable(item.type));
    let timelineData : TimelineData[] = [];
    for (let item of items) {
      for (let entry of this.timelineData.epochDaysToData){
        let value = entry[1];
        let target : number = value.findIndex(tld => tld.itemId === item.id);
        if (target > -1) {
          timelineData.push(value[target]);
          break;
        }
      }
    }
    let data = {
      clickedData : timelineDataArr,
      dataItem :  (timelineItem) ? timelineItem : undefined,
      data: timelineData,
      items: items,
      patientName : this.patientName,
      patientId : this.requestedPatientId,
      scoredSubscriptions : this.scoredSubscriptions,
      min: this.timelineData.range.min,
      max: this.timelineData.range.max
    }

    if (this.isGraphable(type)) {
      this.dialog.open(GraphDetail, {
        width: width,
        height: height,
        data: data
      });
    } else if (type === 'DOCUMENT' && timelineItem) {
      // console.log('Calling Document', data);
      this.dialog.open(DocumentViewer, {
        width: width,
        height: height,
        data: data
      });
    }

  }

  isGraphable(type : string) : boolean {
    return [
      'OBSERVATION', 'SYMPTOM', 'TREATMENT', 'ACTION', 'EVIDENCE', 'COMPASS_SCORE'
    ].indexOf(type) > -1;

  }


  rangeOptions : any[] =  [
    {
      displayName: "Last 30 days",
      value: "last30"
    },
    {
      displayName: "Last 90 days",
      value: "last90"
    },
    {
      displayName: "Last year",
      value: "lastYear"
    },
    {
      displayName: "Since earliest",
      value: "earliest"
    },
    {
      displayName: "Custom",
      value: "custom"
    }
  ];
}




